import { useRef } from 'react'
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion'
import { ArrowRight, MapPin } from 'lucide-react'
import KineticText from '../ui/KineticText'
import KineticButton from '../ui/KineticButton'
import Counter from '../ui/Counter'
import HeroScene from '../ui/HeroScene'
import useParallax from '../../hooks/useParallax'
import usePointerParallax from '../../hooks/usePointerParallax'
import { scrollToSection } from '../../utils/helpers'
import { COMPANY } from '../../data/company'

const EASE = [0.2, 0, 0, 1]

// Real figures, shown in the capability sheet's footer.
const STATS = [
  { value: 175, suffix: 'K+', label: 'end users served' },
  { value: COMPANY.stats.projectsDelivered, suffix: '+', label: 'projects delivered' },
  { value: COMPANY.stats.yearsExperience, suffix: '+', label: 'years in operation' },
]

// What the company does, as a numbered spec rather than marketing tiles.
const CAPABILITIES = [
  { title: 'Product engineering', detail: 'Web · Mobile · Cloud platforms' },
  { title: 'Systems integration', detail: 'APIs · Payments · Data pipelines' },
  { title: 'Delivery', detail: 'Discovery → Build → Operate' },
  { title: 'Support & maintenance', detail: 'SLA-backed, after go-live' },
]

/* The capability sheet: a single outlined surface that reads like an
   engineering spec — a header strip, numbered rows, and the figures across
   the foot. It floats in front of the hero's scene of orbits, stars and
   mathematics. */
function CapabilitySheet() {
  return (
    <div className="surface overflow-hidden rounded-3xl !shadow-[inset_0_1px_0_rgb(var(--highlight)),var(--elev-4)]">
      <div className="band flex items-center justify-between px-6 py-3 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] !shadow-none">
        <span>OrionDev · capability sheet</span>
        <span className="opacity-70">rev {new Date().getFullYear()}</span>
      </div>
      <ol className="divide-y divide-outline-variant">
        {CAPABILITIES.map((c, i) => (
          <li key={c.title} className="grid grid-cols-[2.5rem_1fr] items-baseline gap-x-3 px-6 py-4 transition-colors duration-200 hover:bg-primary-container/40">
            <span className="font-mono text-xs font-semibold text-primary">{String(i + 1).padStart(2, '0')}</span>
            <div>
              <p className="text-[17px] font-semibold tracking-tight text-ink">{c.title}</p>
              <p className="mt-0.5 font-mono text-[12.5px] text-ink-3">{c.detail}</p>
            </div>
          </li>
        ))}
      </ol>
      <dl className="grid grid-cols-3 divide-x divide-outline-variant border-t border-outline-variant bg-sc/60">
        {STATS.map((s) => (
          <div key={s.label} className="px-4 py-4 sm:px-5">
            <dd className="font-mono text-2xl font-bold leading-none tracking-tight text-ink sm:text-[1.7rem]">
              <Counter value={s.value} />
              <span className="text-primary">{s.suffix}</span>
            </dd>
            <dt className="mt-2 text-[12px] leading-snug text-ink-3">{s.label}</dt>
          </div>
        ))}
      </dl>
    </div>
  )
}

export default function Hero() {
  const reduce = useReducedMotion()
  const parallax = useParallax(48)
  const pointer = usePointerParallax()
  const sectionRef = useRef(null)
  const setRefs = (el) => {
    parallax.current = el
    pointer.current = el
    sectionRef.current = el
  }

  // Scrolling past the hero: the copy lifts away and fades, and the sheet
  // tips back into the page — the hero recedes rather than just scrolling off.
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end start'] })
  const copyY = useTransform(scrollYProgress, [0, 1], [0, -70])
  const copyOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0])
  const sheetRotate = useTransform(scrollYProgress, [0, 1], [0, 16])
  const sheetY = useTransform(scrollYProgress, [0, 1], [0, 50])
  const sheetScale = useTransform(scrollYProgress, [0, 1], [1, 0.93])
  const copyStyle = reduce ? undefined : { y: copyY, opacity: copyOpacity }
  const sheetStyle = reduce ? undefined : { rotateX: sheetRotate, y: sheetY, scale: sheetScale, transformPerspective: 1200, transformOrigin: '50% 0%' }
  const enter = (delay) => ({
    initial: reduce ? { opacity: 0 } : { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5, ease: EASE, delay },
  })

  return (
    <section
      ref={setRefs}
      id="home"
      className="relative isolate scroll-mt-24 overflow-hidden border-b border-outline-variant pt-32 pb-20 md:pt-40 md:pb-28"
    >
      <HeroScene />

      {/* A single structural accent: the brand teal along the top edge. */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-primary" aria-hidden="true" />

      <div className="mx-auto grid max-w-6xl items-center gap-14 px-6 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16 lg:px-10 xl:max-w-7xl 2xl:max-w-[90rem] 2xl:px-16">
        <motion.div style={copyStyle} className="text-center lg:text-left">
          <motion.p {...enter(0.05)} className="eyebrow justify-center lg:justify-start">
            Software engineering company
          </motion.p>

          <KineticText
            as="h1"
            immediate
            delay={0.1}
            each={0.04}
            text={['Engineering software that runs', <span key="biz" className="text-primary">your business.</span>]}
            className="mt-6 text-[2.75rem] font-bold leading-[1.02] tracking-[-0.035em] text-ink text-balance sm:text-6xl md:text-[4.25rem] 2xl:text-[5rem]"
          />

          <motion.p
            {...enter(0.35)}
            className="mx-auto mt-7 max-w-xl text-lg leading-relaxed text-ink-3 text-pretty md:text-xl lg:mx-0"
          >
            OrionDev designs, builds and operates web, mobile and cloud systems for organisations
            that need software to work — on schedule, on budget, and in production.
          </motion.p>

          <motion.div
            {...enter(0.45)}
            className="mt-9 flex flex-col items-center gap-3 sm:flex-row sm:justify-center lg:justify-start"
          >
            <KineticButton onClick={() => scrollToSection('contact')} variant="filled" size="lg" className="w-full sm:w-auto">
              Request a quotation
              <ArrowRight className="h-4 w-4" />
            </KineticButton>
            <KineticButton onClick={() => scrollToSection('portfolio')} variant="outlined" size="lg" className="w-full sm:w-auto">
              View our work
            </KineticButton>
          </motion.div>

          <motion.p
            {...enter(0.55)}
            className="mt-8 flex items-center justify-center gap-2 font-mono text-[12.5px] uppercase tracking-[0.12em] text-ink-3 lg:justify-start"
          >
            <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" />
            {COMPANY.contact.location}
          </motion.p>
        </motion.div>

        <motion.div {...enter(0.3)} className="mx-auto w-full max-w-lg lg:max-w-none">
          <motion.div style={sheetStyle}>
            <div className="float-layer">
              <CapabilitySheet />
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}
